// The recovery phrase is never stored in plain text. It is kept encrypted:
//  - once with a key made from your PIN (PBKDF2-SHA256, 600,000 rounds), and
//  - optionally once with a key from Face ID / fingerprint (WebAuthn PRF), so
//    the phone's secure chip has to agree before the phrase can be opened.
// Five wrong PINs within 24 hours deletes both copies from this device; you then
// restore with your 12 words. Contacts and the ledger are kept.

export const PIN_LEN = 6;
export const MAX_FAILS = 5;
export const FAIL_WINDOW_MS = 24 * 60 * 60 * 1000;
const ITER = 600000;
const te = new TextEncoder(), td = new TextDecoder();
const rand = (n) => crypto.getRandomValues(new Uint8Array(n));
const b64 = (u) => btoa(String.fromCharCode(...new Uint8Array(u)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export async function pinKey(pin, salt, iterations = ITER) {
  const base = await crypto.subtle.importKey('raw', te.encode(pin), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

export async function rawKey(bytes, info) {
  const base = await crypto.subtle.importKey('raw', bytes, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: te.encode('drop/vault'), info: te.encode(info) }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

export async function seal(key, text) {
  const iv = rand(12);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, te.encode(text));
  return { iv: b64(iv), ct: b64(ct) };
}

export async function unseal(key, box) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(box.iv) }, key, unb64(box.ct));
  return td.decode(pt);
}

export function validPin(pin) { return new RegExp(`^\\d{${PIN_LEN}}$`).test(pin) && !/^(\d)\1+$/.test(pin) && !['123456', '654321', '012345'].includes(pin); }

export async function createVault(phrase, pin, iterations = ITER) {
  const salt = rand(16);
  const key = await pinKey(pin, salt, iterations);
  return { v: 1, salt: b64(salt), iter: iterations, pinBox: await seal(key, phrase), bio: null };
}

export async function openWithPin(vault, pin) {
  const key = await pinKey(pin, unb64(vault.salt), vault.iter);
  return unseal(key, vault.pinBox); // throws on a wrong PIN
}

// Failure counting: a rolling 24-hour window.
export function registerFail(state, now = Date.now()) {
  const s = state && now - state.first < FAIL_WINDOW_MS ? { ...state } : { count: 0, first: now };
  s.count += 1;
  return { state: s, locked: s.count >= MAX_FAILS, left: Math.max(0, MAX_FAILS - s.count) };
}
export function failsLeft(state, now = Date.now()) {
  if (!state || now - state.first >= FAIL_WINDOW_MS) return MAX_FAILS;
  return Math.max(0, MAX_FAILS - state.count);
}

// ---------------------------------------------------------------- Face ID / fingerprint
export async function bioAvailable() {
  try { return !!window.PublicKeyCredential && await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { return false; }
}

function prfFrom(cred) {
  const r = cred?.getClientExtensionResults?.().prf;
  const first = r?.results?.first;
  return first ? new Uint8Array(first) : null;
}

async function bioGet(credId, prfSalt) {
  const cred = await navigator.credentials.get({ publicKey: {
    challenge: rand(32), rpId: location.hostname, userVerification: 'required', timeout: 60000,
    allowCredentials: [{ type: 'public-key', id: unb64(credId) }],
    extensions: { prf: { eval: { first: unb64(prfSalt) } } },
  } });
  const out = prfFrom(cred);
  if (!out) throw new Error('This device can’t use Face ID or fingerprint to protect your keys. Use your PIN instead.');
  return out;
}

// Returns a new vault with a Face ID copy added.
export async function enableBio(vault, phrase) {
  const prfSalt = b64(rand(32));
  const cred = await navigator.credentials.create({ publicKey: {
    rp: { name: 'Relay', id: location.hostname },
    user: { id: rand(16), name: 'Relay', displayName: 'Relay' },
    challenge: rand(32),
    pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
    authenticatorSelection: { authenticatorAttachment: 'platform', residentKey: 'preferred', userVerification: 'required' },
    timeout: 60000,
    extensions: { prf: { eval: { first: unb64(prfSalt) } } },
  } });
  const credId = b64(cred.rawId);
  const ext = cred.getClientExtensionResults?.().prf;
  if (ext && ext.enabled === false) throw new Error('This device can’t use Face ID or fingerprint to protect your keys. Use your PIN instead.');
  const secret = prfFrom(cred) || await bioGet(credId, prfSalt);
  const key = await rawKey(secret, 'bio');
  return { ...vault, bio: { credId, prfSalt, box: await seal(key, phrase) } };
}

export async function openWithBio(vault) {
  if (!vault.bio) throw new Error('Face ID is not set up.');
  const secret = await bioGet(vault.bio.credId, vault.bio.prfSalt);
  return unseal(await rawKey(secret, 'bio'), vault.bio.box);
}
